/**
 * @file ChangeQuantityModal.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-08
 * @description Change one order line's quantity from a transport planning board.
 *
 *              The same change as editing the order: the line's own price is
 *              kept, a linked PO-D's quantity follows (never its price), the
 *              PO-H is rebuilt, and the POs a supplier already holds come back
 *              in `po_changes` for the board to offer as revised copies —
 *              PATCH /api/orders/customer-orders/:orderId/lines/:lineId/quantity.
 *
 *              <ChangeQuantityModal row={row} onClose={...} onDone={(result) => ...} />
 */

import React, { useState } from 'react';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { unitSuffixFor } from '../utils/units';
import { formatDecimalInput, formatQuantity } from '../utils/numberFormatters';
import './ChangeQuantityModal.css';

function ChangeQuantityBody({ row, onClose, onDone }) {
  const [value, setValue] = useState(formatDecimalInput(parseFloat(row.quantity) || ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useSlideOutButton([saving]);

  const current = parseFloat(row.quantity) || 0;
  const next = parseFloat(value);
  const valid = Number.isFinite(next) && next > 0 && next !== current;

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/orders/customer-orders/${row.id}/lines/${row.order_line_id}/quantity`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: next })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to change the quantity');
      onDone(data);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const unit = unitSuffixFor(row.unit_weight_kg);
  return (
    <>
        <p className="cq-what">
          {row.order_number} · {row.customer_name}
          <br />
          <strong>{row.product_name || row.product_code}</strong>
        </p>
        <label className="cq-label" htmlFor="cq-quantity">New quantity{unit ? ` (${unit.replace(/^× /, '')})` : ''}</label>
        <input
          id="cq-quantity"
          className="styled-input"
          type="number"
          min="0"
          step="any"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
        />
        <p className="cq-note">
          Was {formatQuantity(current)}{unit ? ` ${unit}` : ''}. The order and its purchase order change together, exactly as
          editing the order does; if the supplier already has the PO you will be asked to send the revised one.
        </p>
        {error && <p className="cq-error">{error}</p>}
      {/* Inside the body rather than Modal's footer prop, so the slide-out
          hook runs in the component that renders the buttons. */}
      <div className="cq-actions">
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={save} disabled={!valid || saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving...' : 'Change quantity'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function ChangeQuantityModal({ row, onClose, onDone }) {
  return (
    <Modal isOpen={!!row} onClose={onClose} title="Change Quantity" size="sm" className="change-quantity">
      {row && <ChangeQuantityBody row={row} onClose={onClose} onDone={onDone} />}
    </Modal>
  );
}

export default ChangeQuantityModal;
